import { useMemo, useState } from "react";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useLocale } from "lib/i18n/client";
import { getTranslations } from "lib/i18n/dictionary";
import { withLocale } from "lib/i18n/path";
import { getPublicSiteOrigin } from "lib/public-site";

const ADMIN_COOKIE_NOTICE_DISMISSED_KEY = "admin-auth-cookie-notice-dismissed";

export function AdminCookieNotice() {
  const locale = useLocale();
  const translations = getTranslations(locale);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return window.localStorage.getItem(ADMIN_COOKIE_NOTICE_DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const privacyPolicyUrl = useMemo(
    () => `${getPublicSiteOrigin()}${withLocale("/privacy-policy", locale)}`,
    [locale]
  );

  const handleDismiss = () => {
    setIsDismissed(true);

    try {
      window.localStorage.setItem(ADMIN_COOKIE_NOTICE_DISMISSED_KEY, "true");
    } catch {
      // Ignore storage failures and keep the notice dismissed for the current session.
    }
  };

  if (isDismissed) {
    return null;
  }

  return (
    <SoftBox
      position="fixed"
      right={0}
      bottom={0}
      left={0}
      px={{ xs: 2, sm: 3, lg: 4 }}
      py={1.5}
      data-testid="admin-cookie-notice"
      sx={({ palette, zIndex }) => ({
        backgroundColor: palette.info.main,
        boxShadow: "0 -10px 30px rgba(15, 23, 42, 0.18)",
        zIndex: zIndex.snackbar,
      })}
    >
      <SoftBox
        display="flex"
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        gap={1.5}
        width="100%"
      >
        <SoftTypography
          variant="button"
          color="white"
          sx={{ lineHeight: 1.6, pr: 1 }}
        >
          {translations.authPage.cookieNotice}&nbsp;
          <SoftTypography
            component="a"
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer"
            variant="button"
            color="white"
            fontWeight="bold"
            sx={{ textDecoration: "underline" }}
          >
            {translations.layout.footerPrivacy}
          </SoftTypography>
        </SoftTypography>
        <SoftBox
          component="button"
          type="button"
          onClick={handleDismiss}
          aria-label={translations.auth.closeButton}
          data-testid="admin-cookie-notice-dismiss"
          sx={{
            appearance: "none",
            border: 0,
            background: "transparent",
            color: ({ palette }) => palette.primary.contrastText,
            cursor: "pointer",
            fontSize: "1.25rem",
            lineHeight: 1,
            p: 0,
            ml: "auto",
            flexShrink: 0,
          }}
        >
          &times;
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}
